import React, { useState } from "react";

import {
  installationHealthStatusBackgroundClass,
} from "@/installation/installation";
import { useNavigate } from "@tanstack/react-router";
import { allRefrigerantAssetsAtLocation, LocationData } from "@/location/location";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSelectedOrganizationId } from "@/organization/api";
import { useLocations, useUpdateLocationActive } from "@/location/api";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { AssetsEnergyConsumptionCard } from "@/refrigerant/AssetsEnergyConsumptionCard";
import { useAuthentication } from "@/contexts/AuthenticationContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellOff } from "lucide-react";
import { BellButton } from "@/components/snooze/BellButton";
import { LocationSnoozeSheetWrapper } from "@/components/snooze/LocationSnoozeSheetWrapper";
import { SnoozedSectionDivider } from "@/components/snooze/SnoozedSectionDivider";

const LocationsPageTableColumns = (
  isStaff: boolean,
  onToggleActive?: (locationId: number, isActive: boolean) => void,
  onSnoozeClick?: (location: LocationData) => void,
): ColumnDef<LocationData>[] => [
  {
    id: "name",
    accessorKey: "name",
    enableSorting: true,
    header: "Location",
    cell: ({ row }) => {
      return (
        <div className={`flex flex-col ${!row.original.is_active ? "opacity-50" : ""}`}>
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {!row.original.is_active && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Inactive</Badge>
            )}
            {row.original.is_snoozed && (
              <BellOff size={14} className="text-snooze-purple shrink-0" />
            )}
          </div>
          <div className="text-muted-foreground">
            {row.original.address_line_1}
          </div>
        </div>
      );
    },
  },
  {
    id: "health_status",
    accessorKey: "health_status",
    enableSorting: true,
    header: "Health status",
    cell: ({ row }) => {
      if (!row.original.is_active) {
        return <span className="text-muted-foreground text-sm">-</span>;
      }
      return (
        <div
          className={`h-3 w-3 rounded-full ${installationHealthStatusBackgroundClass[row.original.health_status] ?? "bg-gray-300"}`}
        />
      );
    },
  },
  {
    id: "equipment_count",
    enableSorting: false,
    header: "Equipment count",
    accessorFn: (row) => row.installation_set.length
  },
  {
    id: "energy_consumption",
    enableSorting: false,
    header: "Energy consumption",
    cell: ({ row }) => {
      return allRefrigerantAssetsAtLocation(row.original).length > 0 ? (
        <AssetsEnergyConsumptionCard assets={row.original.installation_set} location={row.original} />
      ) : (
        <div>-</div>
      );
    },
  },
  {
    id: "snooze",
    enableSorting: false,
    header: "",
    cell: ({ row }: { row: any }) => {
      if (!row.original.is_active) return null;
      return (
        <BellButton
          isSnoozed={row.original.is_snoozed}
          size="sm"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onSnoozeClick?.(row.original);
          }}
        />
      );
    },
  } as ColumnDef<LocationData>,
  ...(isStaff ? [{
    id: "active_toggle",
    enableSorting: false,
    header: "Active",
    cell: ({ row }: { row: any }) => {
      return (
        <Switch
          checked={row.original.is_active}
          onCheckedChange={(checked: boolean) => {
            onToggleActive?.(row.original.id, checked);
          }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      );
    },
  } as ColumnDef<LocationData>] : []),
];


const LocationsPage = () => {
  const organizationId = useSelectedOrganizationId();
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const isStaff = user?.is_staff ?? false;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [snoozeTarget, setSnoozeTarget] = useState<LocationData | null>(null);
  const { mutate: updateActive } = useUpdateLocationActive();

  const { data, isLoading } = useLocations({
    page: 1,
    pageSize: 100,
    sorting,
    filters: {
      organization_id: organizationId,
      ...(showInactive && { include_inactive: true }),
    },
  });

  const handleToggleActive = (locationId: number, isActive: boolean) => {
    updateActive(
      { location_id: locationId, is_active: isActive },
      {
        onSuccess: () => toast.success(isActive ? "Location activated" : "Location deactivated"),
        onError: () => toast.error("Failed to update location status"),
      }
    );
  };

  const allLocations = data?.results || [];
  // Split into non-snoozed and snoozed — both can be active or inactive independently
  const nonSnoozedLocations = allLocations.filter(loc => !loc.is_snoozed);
  const snoozedLocations = allLocations.filter(loc => loc.is_snoozed);

  const handleRowClick = (row: { original: LocationData }) => {
    if (!row.original.is_active) {
      toast("Location not active");
      return;
    }
    navigate({ to: "/organization/$organizationId/locations/$locationId", params: { locationId: row.original.id, organizationId }});
  };

  return (
    <>
      <Card className="">
        <div className="flex items-center justify-between p-3 lg:p-4">
          <h2 className="font-semibold text-lg">Locations</h2>
          {isStaff && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              Show inactive
            </label>
          )}
        </div>
        <div className="flex flex-col gap-4 p-3 lg:p-4 pt-0 lg:pt-0">
          {nonSnoozedLocations.length > 0 && (
            <div>
              <ThaloDataTable
                variant="thalo"
                data={nonSnoozedLocations}
                columns={LocationsPageTableColumns(isStaff, handleToggleActive, setSnoozeTarget)}
                isLoading={isLoading}
                pageCount={1}
                itemsPerPage={100}
                showColumnFilter={false}
                sorting={sorting}
                onSortingChange={(newSorting: SortingState) => {
                  setSorting(newSorting);
                }}
                onRowClick={handleRowClick}
              />
            </div>
          )}

          {/* Snoozed locations — shown at bottom, muted */}
          {snoozedLocations.length > 0 && (
            <div>
              <SnoozedSectionDivider count={snoozedLocations.length} />
              <div className="opacity-50">
                <ThaloDataTable
                  variant="thalo"
                  data={snoozedLocations}
                  columns={LocationsPageTableColumns(isStaff, handleToggleActive, setSnoozeTarget)}
                  isLoading={false}
                  pageCount={1}
                  itemsPerPage={100}
                  showColumnFilter={false}
                  sorting={sorting}
                  onSortingChange={(newSorting: SortingState) => {
                    setSorting(newSorting);
                  }}
                  onRowClick={handleRowClick}
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {snoozeTarget && (
        <LocationSnoozeSheetWrapper
          locationId={snoozeTarget.id}
          locationName={snoozeTarget.name}
          isSnoozed={snoozeTarget.is_snoozed}
          open={true}
          onOpenChange={(open) => { if (!open) setSnoozeTarget(null); }}
        />
      )}
    </>
  );
};

export default LocationsPage;
